import React, { useState } from "react";
import styled from "@emotion/styled";

import {
  Divider as MuiDivider,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";

import { RootState } from "../../../redux/store";
import { Bookmark } from "../../../types/app";
import BookmarkGroupChip from "./BookmarkGroupChip";
import useAppSelector from "../../../hooks/useAppSelector";
import BookmarkGroupDialog from "./BookmarkGroupDialog";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { setCurrentSelectedBookmarks } from "../../../redux/slices/bookmark";

const Divider = styled(MuiDivider)(spacing);

const Card = styled(MuiCard)`
  height: 100%;
  border-radius: 15px;
  box-shadow: 0px 0px 5px -1px rgba(0, 0, 0, 0.2);
`;

const CardContent = styled(MuiCardContent)`
  padding: 20px;
`;

const Bookmarks = styled.div`
  padding: 15px;
  margin-top: 10px;
  border-radius: 10px;
  cursor: pointer;
  background-color: ${(props) => props.theme.palette.background.default};
  &:hover {
    background-color: ${(props) => props.theme.sidebar.color};
  }
`;

const BookmarkContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SourceText = styled(Typography)`
  font-size: 9px;
  margin-top: 5px;
  word-wrap: break-word;
`;

interface Props {
  groupName: string;
  bookmarks: Bookmark[];
}

function BookmarkGroup(props: Props) {
  const { groupName, bookmarks } = props;
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const dispatch = useAppDispatch();
  const color = bookmarkState.colorMapping[groupName];
  const [open, setOpen] = useState(false);
  const bookmarksDisplay: Record<string, Bookmark[]> = {};
  bookmarks.forEach((bookmark) => {
    if (bookmarksDisplay[bookmark.title] === undefined) {
      bookmarksDisplay[bookmark.title] = [bookmark];
    } else {
      bookmarksDisplay[bookmark.title].push(bookmark);
    }
  });

  function onClick(bookmarkTitle: string) {
    setOpen(true);
    dispatch(
      setCurrentSelectedBookmarks({ title: bookmarkTitle, group: groupName })
    );
  }

  function uniqueBookmarkSource(title: string) {
    return Array.from(
      new Set(bookmarksDisplay[title].map((bookmark) => bookmark.pdfName))
    ).join(", ");
  }

  return (
    <Card>
      <CardContent>
        <BookmarkGroupChip label={groupName} color={color} />
        <BookmarkContainer>
          {Object.keys(bookmarksDisplay).map((title) => {
            const bookmark = bookmarksDisplay[title][0];
            return (
              <Bookmarks key={title} onClick={() => onClick(title)}>
                <Typography fontWeight="bold" fontSize="14.5px">
                  {title}
                </Typography>
                <Typography sx={{ marginTop: "3px" }}>
                  {bookmark.description}
                </Typography>
                <Divider my={3} />
                <SourceText>
                  {`Source: ${uniqueBookmarkSource(title)}`}
                </SourceText>
              </Bookmarks>
            );
          })}
        </BookmarkContainer>
      </CardContent>
      <BookmarkGroupDialog open={open} setOpen={setOpen} />
    </Card>
  );
}

export default BookmarkGroup;
